import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api'

/**
 * Which league and season you are looking at, and how to look at another.
 *
 * **A switcher, not a path.** The navigation research is consistent on this: something true
 * everywhere in an app belongs in the chrome, and a hierarchy is only worth walking when there is
 * genuinely a choice at each level. Here there almost never is — one house league, one live season —
 * so the common case renders as a plain heading and the menu only appears when there is somewhere
 * else to go.
 */
export function SeasonSwitcher({ leagueId, seasonId, go }: {
  leagueId: number
  seasonId: number
  go: (...segments: (string | number)[]) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)

  const leagues = useQuery({ queryKey: ['fantasy', 'mine'], queryFn: api.myFantasyLeagues })
  const seasons = useQuery({
    queryKey: ['fantasy', 'seasons', leagueId],
    queryFn: () => api.seasons(leagueId),
  })

  useEffect(() => {
    if (!open) return

    const dismiss = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false)
    }

    // Pointer anywhere else closes it, and so does Escape — a menu you can only close by choosing
    // something is a trap on a phone, where there is no stray corner to click.
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', dismiss)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', dismiss)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  const league = (leagues.data ?? []).find((entry) => entry.id === leagueId)
  const season = (seasons.data ?? []).find((entry) => entry.id === seasonId)

  const otherLeagues = (leagues.data ?? []).filter((entry) => entry.id !== leagueId)
  const otherSeasons = (seasons.data ?? []).filter((entry) => entry.id !== seasonId)

  const title = season?.name ?? 'Season'

  return (
    <div ref={wrapper} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex min-w-0 items-center gap-1.5 text-left"
      >
        <span className="min-w-0 truncate text-2xl font-semibold">{title}</span>
        <span aria-hidden="true" className="shrink-0 text-sm text-ink-faint">
          ▾
        </span>
      </button>

      <div className="truncate text-xs text-ink-faint">{league?.name ?? 'Fantasy League'}</div>

      {open ? (
        <div className="absolute left-0 z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface p-1 shadow-lg">
          {otherSeasons.length > 0 ? (
            <>
              <p className="px-2 pb-1 pt-2 text-[10px] uppercase tracking-wide text-ink-faint">
                Other seasons
              </p>
              {otherSeasons.map((entry) => (
                <MenuItem
                  key={entry.id}
                  onClick={() => {
                    setOpen(false)
                    go(leagueId, entry.id)
                  }}
                >
                  {entry.name}
                </MenuItem>
              ))}
            </>
          ) : null}

          {otherLeagues.length > 0 ? (
            <>
              <p className="px-2 pb-1 pt-2 text-[10px] uppercase tracking-wide text-ink-faint">
                Other leagues
              </p>
              {otherLeagues.map((entry) => (
                <MenuItem
                  key={entry.id}
                  onClick={() => {
                    setOpen(false)
                    go(entry.id)
                  }}
                >
                  {entry.name}
                </MenuItem>
              ))}
            </>
          ) : null}

          <div className="mt-1 border-t border-line pt-1">
            <MenuItem
              onClick={() => {
                setOpen(false)
                go('leagues')
              }}
            >
              Leagues and invites…
            </MenuItem>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-11 w-full min-w-0 items-center rounded px-2 text-left text-sm text-ink-soft hover:bg-surface-raised hover:text-ink sm:min-h-9"
    >
      <span className="min-w-0 truncate">{children}</span>
    </button>
  )
}
